import React,{useEffect} from "react";

const FontSizeDropdown: React.FC<{ editor: any }> = ({ editor }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [customSize, setCustomSize] = React.useState('');
  const [currentSize, setCurrentSize] = React.useState('16');
  
  const sizes = [
    { name: '8', value: '8px' },
    { name: '9', value: '9px' },
    { name: '10', value: '10px' },
    { name: '11', value: '11px' },
    { name: '12', value: '12px' },
    { name: '14', value: '14px' },
    { name: '16', value: '16px' },
    { name: '18', value: '18px' },
    { name: '20', value: '20px' },
    { name: '24', value: '24px' },
    { name: '28', value: '28px' },
    { name: '32', value: '32px' },
    { name: '36', value: '36px' },
    { name: '48', value: '48px' },
    { name: '72', value: '72px' },
  ];

  const getCurrentSize = React.useCallback(() => {
    const selection = editor.state.selection;
    const { from, to } = selection;
    let fontSize = '16';
    
    // Check if there's an active textStyle mark with fontSize
    if (editor.isActive('textStyle')) {
      const attributes = editor.getAttributes('textStyle');
      if (attributes.fontSize) {
        fontSize = attributes.fontSize.replace('px', '');
        return fontSize;
      }
    }
    
    // Fallback: check nodes in selection
    editor.state.doc.nodesBetween(from, to, (node: any) => {
      if (node.marks) {
        node.marks.forEach((mark: any) => {
          if (mark.type.name === 'textStyle' && mark.attrs.fontSize) {
            fontSize = mark.attrs.fontSize.replace('px', '');
          }
        });
      }
    });
    
    return fontSize;
  }, [editor]);

  // Update current size when editor changes
  useEffect(() => {
    const updateCurrentSize = () => {
      setCurrentSize(getCurrentSize());
    };

    // Listen to editor updates
    if (editor) {
      editor.on('selectionUpdate', updateCurrentSize);
      editor.on('transaction', updateCurrentSize);
      
      // Initial update
      updateCurrentSize();
    }

    return () => {
      if (editor) {
        editor.off('selectionUpdate', updateCurrentSize);
        editor.off('transaction', updateCurrentSize);
      }
    };
  }, [editor, getCurrentSize]);

  const applyFontSize = (size: string) => {
    if (editor.commands.setFontSize) {
      editor.chain().focus().setFontSize(size).run();
    } else {
      // Fallback: use textStyle mark
      editor.chain().focus().setMark('textStyle', { fontSize: size }).run();
    }
  };

  const applyCustomSize = () => {
    if (customSize) {
      const size= parseInt(customSize);

      if(size>0 && size<=100){
        applyFontSize(`${size}px`);
      }else{
        applyFontSize(`100px`);
      }
      setCustomSize('');
      setIsOpen(false);
    }
  };

  const handleCustomSizeKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      applyCustomSize();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isOpen && !target.closest('.dropdown')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  
  return (
    <div className="dropdown font-size-dropdown">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="btn btn-sm btn-outline-secondary d-flex align-items-center px-2"
        style={{ width: '60px' }}
        title="Font Size"
      >
        <span className="small fs-15 flex-fill text-center">{currentSize}</span>
        <i className="ti ti-chevron-down fs-14 flex-shrink-0"></i>
      </button>
      
      {isOpen && (
        <div className="dropdown-menu show position-absolute mt-1 p-2" style={{ minWidth: '120px', zIndex: 1000 }}>
          {/* Custom Size Input */}
          <div className="mb-2">
            <label className="form-label fs-12 mb-1">Custom Size (px):</label>
            <div className="input-group input-group-sm">
              <input
                type="number"
                className="form-control form-control-sm"
                placeholder="Enter size"
                value={customSize}
                onChange={(e) => setCustomSize(e.target.value)}
                onKeyPress={handleCustomSizeKeyPress}
                min="1"
                max="200"
              />
              <button
                className="btn btn-outline-secondary btn-sm"
                type="button"
                onClick={applyCustomSize}
                disabled={!customSize || parseInt(customSize) <= 0}
              >
                <i className="ti ti-check fs-12"></i>
              </button>
            </div>
          </div>
          
          <hr className="my-2" />
          
          {/* Preset Sizes */}
          <div className="preset-sizes" style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {sizes.map((size, index) => (
              <button
                key={index}
                onClick={() => {
                  applyFontSize(size.value);
                  setIsOpen(false);
                }}
                className={`dropdown-item text-center ${currentSize === size.name ? 'active' : ''}`}
              >
                {size.name}px
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FontSizeDropdown;
